// core/utils/read-only-session.ts
//
// Read-only admin sessions. user-service marks the access token of a merchant
// whose store is SUSPENDED with the claims `readOnly: true` and
// `readOnlyReason: 'SUSPENDED'`. Every backend service enforces it (403
// `{ error: 'read_only' }` on writes); the admin only DISPLAYS it: a persistent
// banner (LayoutComponent) and a toast on a blocked write (ErrorInterceptor).
// Nothing here is a security boundary — the token is decoded, not verified.
// Tokens without `readOnly: true` (every token today) change nothing.

export interface ReadOnlySession {
  readOnly: true;
  reason: string | null;
}

export const READ_ONLY_ERROR_CODE = 'read_only';
export const READ_ONLY_BANNER_TEXT =
  'Your store is suspended. You have read-only access. Contact Socommerz support.';
export const READ_ONLY_TOAST_MESSAGE = 'This store is suspended. Your access is read-only.';

export function decodeJwtPayload(token: unknown): any | null {
  if (typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

// The access token as the app stores it: localStorage 'token' holds a JSON string.
export function storedAccessToken(): string | null {
  try {
    const raw = localStorage.getItem('token');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

export function readOnlySessionFromToken(token: unknown): ReadOnlySession | null {
  const claims = decodeJwtPayload(token);
  if (!claims || claims.readOnly !== true) return null;
  return { readOnly: true, reason: claims.readOnlyReason || null };
}

export function currentReadOnlySession(): ReadOnlySession | null {
  return readOnlySessionFromToken(storedAccessToken());
}

export function isReadOnlyError(error: any): boolean {
  return error?.status === 403 && error?.error?.error === READ_ONLY_ERROR_CODE;
}
